/**
 * LA PIEL DEL NEGOCIO (rebranding macOS 27, punto 11, 21-oct-2026).
 *
 * Dentro del negocio de un cliente manda SU color: sale de `colorMarca` y pisa las fichas del
 * acento (`--acento*`, `--marca-*`, `--mac-tintado*`) que `index.css` trae con el magenta de
 * Silva Notion. Sin color guardado no se pisa nada: queda el magenta (botón lleno #D61F72).
 *
 * Todo tono que lleva letra encima se calcula para dar ≥ 4,5:1 (WCAG AA), en claro y en oscuro.
 * PURO: sin DOM, para probarlo con números.
 */

type Rgb = readonly [number, number, number];

const BLANCO: Rgb = [255, 255, 255];
const NEGRO: Rgb = [0, 0, 0];
/** Ventana oscura (#1E1E1E) y barra lateral clara (#E6E6E6), las de `index.css`. */
const VENTANA_OSCURA: Rgb = [30, 30, 30];
const LATERAL: Rgb = [230, 230, 230];

export function hexARgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export const aHex = (c: Rgb): string => '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');

function luminancia(c: Rgb): number {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contraste WCAG entre dos colores (1 a 21). */
export function contraste(a: Rgb, b: Rgb): number {
  const [x, y] = [luminancia(a), luminancia(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

const mezclar = (a: Rgb, b: Rgb, t: number): Rgb =>
  a.map((v, i) => Math.round(v + (b[i]! - v) * t)) as unknown as Rgb;

/** Lleva `color` hacia blanco o negro (lo contrario de `fondo`) lo justo para dar `minimo` sobre `fondo`. */
function conContraste(color: Rgb, fondo: Rgb, minimo: number): Rgb {
  const hacia = luminancia(fondo) > 0.18 ? NEGRO : BLANCO;
  for (let i = 0; i <= 100; i++) {
    const c = mezclar(color, hacia, i / 100);
    if (contraste(c, fondo) >= minimo) return c;
  }
  return hacia;
}

export interface TonosDeMarca {
  /** Relleno de los botones llenos y del sello, y su letra (≥ 4,5:1 entre los dos). */
  lleno: Rgb;
  letra: Rgb;
  pulsado: Rgb;
  claro: { acento: Rgb; texto: Rgb; icono: Rgb; tintado: Rgb; tintadoPulsado: Rgb };
  oscuro: { acento: Rgb; texto: Rgb; icono: Rgb; tintado: Rgb; tintadoPulsado: Rgb };
  /** La escala `brand-50…900` de Tailwind. */
  escala: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, Rgb>;
}

/** Los tonos que salen de un color de marca, o `null` si no es un `#rrggbb`. PURO. */
export function tonosDeMarca(hex: string): TonosDeMarca | null {
  const base = hexARgb(hex);
  if (!base) return null;

  // Letra blanca si el color ya da 3:1 con blanco (basta oscurecerlo un poco y sigue siendo el
  // mismo color, como el magenta → #D61F72); si no (amarillos, verdes claros…), letra negra.
  const letra = contraste(base, BLANCO) >= 3 ? BLANCO : NEGRO;
  const lleno = conContraste(base, letra, 4.5);
  const pulsado = mezclar(lleno, letra === BLANCO ? NEGRO : BLANCO, 0.1);

  const texto = conContraste(base, BLANCO, 4.5); // enlace sobre blanco
  const icono = conContraste(base, LATERAL, 3); // ícono sobre la barra lateral
  const acentoOscuro = conContraste(base, VENTANA_OSCURA, 4.5);
  const textoOscuro = conContraste(base, VENTANA_OSCURA, 6);

  return {
    lleno,
    letra,
    pulsado,
    claro: { acento: base, texto, icono, tintado: mezclar(BLANCO, base, 0.2), tintadoPulsado: mezclar(BLANCO, base, 0.3) },
    oscuro: {
      acento: acentoOscuro,
      texto: textoOscuro,
      icono: acentoOscuro,
      tintado: mezclar(VENTANA_OSCURA, acentoOscuro, 0.2),
      tintadoPulsado: mezclar(VENTANA_OSCURA, acentoOscuro, 0.26),
    },
    escala: {
      50: mezclar(BLANCO, base, 0.06),
      100: mezclar(BLANCO, base, 0.11),
      200: mezclar(BLANCO, base, 0.22),
      300: textoOscuro,
      400: acentoOscuro,
      500: base,
      600: icono,
      700: texto,
      800: mezclar(texto, NEGRO, 0.25),
      900: mezclar(texto, NEGRO, 0.4),
    },
  };
}

const canales = (c: Rgb) => c.join(' ');

/** La hoja que pisa las fichas de `index.css` con el color del cliente; '' si el color no sirve. PURO. */
export function cssDeMarca(hex: string): string {
  const t = tonosDeMarca(hex);
  if (!t) return '';
  const comunes = [
    `--acento-lleno:${canales(t.lleno)}`,
    `--acento-sobre:${canales(t.letra)}`,
    `--acento-pulsado:${aHex(t.pulsado)}`,
    ...Object.entries(t.escala).map(([k, c]) => `--marca-${k}:${canales(c)}`),
  ];
  const tema = (x: TonosDeMarca['claro']) => [
    `--acento:${canales(x.acento)}`,
    `--acento-texto:${canales(x.texto)}`,
    `--acento-icono:${canales(x.icono)}`,
    `--mac-tintado:${aHex(x.tintado)}`,
    `--mac-tintado-pulsado:${aHex(x.tintadoPulsado)}`,
  ];
  return `:root{${[...comunes, ...tema(t.claro)].join(';')}}.dark{${tema(t.oscuro).join(';')}}`;
}
